import { Context } from "./Provider";
import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  removeMainCat,
  getFullState,
  useRemovePageQSFromURL,
  scrollToTopOfJobs,
} from "../helpers/helpers.js";

if (typeof window.kw_from_search === "undefined") window.kw_from_search = "";
if (typeof window.predefined_cats === "undefined") window.predefined_cats = "";

const truePageURL =
  window.location.protocol +
  "//" +
  window.location.host +
  window.location.pathname;

const JobSearch = () => {
  const { data, loading, updateJobParams, updatePageIndex } =
    useContext(Context);
  const [skipKeywordFromSearch, setSkipKeywordFromSearch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const removePageQSFromURL = useRemovePageQSFromURL();
  const [activeHeaders, setActiveHeaders] = useState({});
  const [isMobileHeaderAnimating, setIsMobileHeaderAnimating] = useState(false);

  // Add a ref to prevent infinite loops
  const handledUrlRef = useRef(false);

  const pathname = location.pathname;

  // Determine base path for link inheritance
  const siteURL = window.location.origin;
  let basePath = pathname.replace(/\/+$/, ""); // Remove trailing slashes
  const isStatePage = pathname.includes("/state-");
  const isCityPage = pathname.includes("/city-");
  const isCategoryPage = !isStatePage && !isCityPage && pathname.split('/').filter(Boolean).length > 1;


  // Extract category segment if present
  let categorySegment = "";
  const pathParts = pathname.split("/").filter((part) => part);
  const pharmacyJobsIndex = pathParts.indexOf("pharmacy-jobs");
  if (pharmacyJobsIndex !== -1 && pathParts.length > pharmacyJobsIndex + 1) {
    const nextPart = pathParts[pharmacyJobsIndex + 1];
    if (nextPart && !nextPart.startsWith("state-") && !nextPart.startsWith("city-")) {
      categorySegment = `/${nextPart}`;
    }
  }

  // Set basePath, preserving category segment if present
  if (isStatePage || isCityPage) {
    basePath = `/pharmacy-jobs${categorySegment}`; // Include category segment
  } else if (categorySegment) {
    basePath = `/pharmacy-jobs${categorySegment}`; // Preserve category for non-state/city pages
  } else {
    basePath = "/pharmacy-jobs"; // Default to base path if no category
  }

  // URL generation functions for crawlable links (state and city only)
  const generateStateLink = (stateAbbr) => {
    const stateName = getFullState(stateAbbr);
    const stateSlug = stateName.toLowerCase().replace(/\s+/g, "-");
    return `${siteURL}${basePath}/state-${stateSlug}/`;
  };

  const generateCityLink = (cityKey, stateAbbr) => {
    const citySlug = cityKey.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    const stateName = getFullState(stateAbbr);
    const stateSlug = stateName.toLowerCase().replace(/\s+/g, "-");
    return `${siteURL}${basePath}/city-${citySlug}-state-${stateSlug}/`;
  };

  const hasAnyAttribute = (element, attributes) => {
    return attributes.some((attr) => element.hasAttribute(attr));
  };

  const closeFilter = () => {
    document.body.classList.remove("job-filter-active");
  };

  useEffect(() => {
    const getNextElementSibling = (element) => {
      return element.nextElementSibling || element.nextSibling;
    };

    const hideFilter = (event) => {
      if (event && event.target && !event.target.matches(".clear-filters")) {
        event.preventDefault();
        closeFilter();
      }
    };

    const handleBodyClick = (event) => {
      const dropdown = event.target.closest(".custom-multichk"),
        dropdownDetail = event.target.closest(".custom-multichk-detail"),
        dropdownPositionElement = event.target.closest(".positioning-wrap");

      if (dropdownPositionElement) {
        return;
      }

      if (dropdown) {
        const dropdownType = dropdown.getAttribute("data-select");
        setActiveHeaders((prevState) => {
          const newState = { ...prevState };
          Object.keys(prevState).forEach((key) => {
            if (key !== dropdownType) {
              newState[key] = false;
            }
          });
          return newState;
        });
      } else if (
        !hasAnyAttribute(event.target, [
          "data-type",
          "data-drop",
          "data-select",
        ])
      ) {
        setActiveHeaders({});
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setActiveHeaders({});
      }
    };

    document.body.addEventListener("mouseup", handleBodyClick);
    document.body.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.body.removeEventListener("mouseup", handleBodyClick);
      document.body.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  // New combined useEffect hook to handle all URL types
  useEffect(() => {
    if (handledUrlRef.current) {
      return;
    }

    if (!loading && data?.aggregations) {
      const pathname = location.pathname;
      const pathParts = pathname.split('/').filter(Boolean);

      // Find the segments for category, state, and city
      let categorySlug = null;
      let stateSlug = null;
      let citySlug = null;

      // Extract category slug from the first segment after "pharmacy-jobs"
      const jobsIndex = pathParts.indexOf("pharmacy-jobs");
      if (jobsIndex !== -1 && pathParts.length > jobsIndex + 1) {
        const possibleCategory = pathParts[jobsIndex + 1];
        if (!possibleCategory.startsWith("state-") && !possibleCategory.startsWith("city-")) {
          categorySlug = possibleCategory;
        }
      }

      // Extract state and city slugs from the full path
      const stateMatch = pathname.match(/\/state-([a-z0-9-]+)\/?/);
      if (stateMatch) {
        stateSlug = stateMatch[1];
      }

      const cityMatch = pathname.match(/\/city-([a-z0-9-]+)-state-([a-z0-9-]+)\/?/);
      if (cityMatch) {
        citySlug = cityMatch[1];
        stateSlug = cityMatch[2]; // Re-assign state slug from the city match to be sure
      }

      // Create a map to easily get the abbreviation from the slug
      const slugToAbbrMap = {
        alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
        colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
        hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
        kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
        massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
        missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new-hampshire': 'NH',
        'new-jersey': 'NJ', 'new-mexico': 'NM', 'new-york': 'NY', 'north-carolina': 'NC',
        'north-dakota': 'ND', ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA',
        'rhode-island': 'RI', 'south-carolina': 'SC', 'south-dakota': 'SD',
        tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT', virginia: 'VA',
        washington: 'WA', 'west-virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
      };
      
      const stateAbbr = stateSlug ? slugToAbbrMap[stateSlug] : null;

      // Find the category in the data
      let foundCategory = null;
      if (categorySlug && data.aggregations.categorieslist?.buckets) {
        foundCategory = data.aggregations.categorieslist.buckets.find(cat => 
          cat.key.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") === categorySlug
        );
      }

      // Find the state in the data
      let foundState = null;
      if (stateAbbr && data.aggregations.stateslist?.buckets) {
        foundState = data.aggregations.stateslist.buckets.find(s => s.key === stateAbbr);
      }
      
      // Update state based on findings
      if (foundCategory || foundState) {
        const newCheckboxes = { ...checkboxes };
        const newSelectLabels = { ...selectLabels };
        let citiesForState = [];
        let shouldSubmit = false;

        if (foundCategory) {
          const categoryIndex = data.aggregations.categorieslist.buckets.findIndex(cat => cat.key === foundCategory.key);
          newCheckboxes[`cat${categoryIndex}`] = 'checked';
          newSelectLabels[foundCategory.key] = 'cat';
          shouldSubmit = true;
        }

        if (foundState) {
          const stateIndex = data.aggregations.stateslist.buckets.findIndex(s => s.key === stateAbbr);
          newCheckboxes[`st${stateIndex}`] = 'checked';
          newSelectLabels[foundState.key] = 'state';
          
          if (foundState['city-list']?.buckets) {
            citiesForState = foundState['city-list'].buckets.map(city => ({
              ...city,
              state: foundState.key,
            }));
            setCityCheckboxValues(citiesForState);

            // Find and check the city if it exists in the URL
            if (citySlug) {
              const foundCity = citiesForState.find(city => 
                city.key.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") === citySlug
              );
              if (foundCity) {
                const cityIndex = citiesForState.findIndex(c => c.key === foundCity.key);
                newCheckboxes[`city${cityIndex}`] = 'checked';
                newSelectLabels[foundCity.key] = 'city';
              }
            }
          }
          shouldSubmit = true;
        }

        if (shouldSubmit) {
          setCheckboxes(newCheckboxes);
          setSelectLabels(newSelectLabels);

          handledUrlRef.current = true;
          
          setTimeout(() => {
            handleSubmit();
          }, 0);
        }
      }
    }
  }, [loading, pathname, data]);

  if (window.kw_from_search && !loading) {
    if (skipKeywordFromSearch === true) {
      window.kw_from_search = "";
    } else {
      setSkipKeywordFromSearch(true);
    }
  }

  const [inputs, setInputs] = useState({ keywords: window.kw_from_search });
  const [checkboxes, setCheckboxes] = useState({});
  const [tags, setTags] = useState([]);
  const [cityCheckboxValues, setCityCheckboxValues] = useState([]);
  const [stateCheckboxValues, setStateCheckboxValues] = useState([]);
  const [categoryCheckboxValues, setCategoryCheckboxValues] = useState([]);
  const [selectLabels, setSelectLabels] = useState({});

  const handleSelectLabels = (type, default_string) => {
    let count = 0,
      label = "",
      final_output = "";

    Object.entries(selectLabels).forEach((entry) => {
      const [key, value] = entry;
      if (value === type) {
        count++;
        label = key;
      }
    });

    if (type === "cat") {
      final_output = count === 1 ? removeMainCat(label) : `(${count} Selected)`;
    } else if (type === "state") {
      final_output = count === 1 ? getFullState(label) : `(${count} Selected)`;
    } else if (type === "city") {
      final_output = count === 1 ? label : `(${count} Selected)`;
    } else {
      final_output = `(${count})`;
    }

    return (
      <span className="check-label" id={`${type}-filter-instructions`}>
        {count > 0 ? final_output : default_string}
      </span>
    );
  };

  const saveDropDownData = () => {
    data.aggregations.stateslist.buckets?.forEach((state) => {
      let check_name_match = (obj) => obj.key === state.key;
      if (!stateCheckboxValues.some(check_name_match))
        setStateCheckboxValues((values) => [...values, state]);
    });

    data.aggregations.categorieslist.buckets?.forEach((cat) => {
      let check_name_match = (obj) => obj.key === cat.key;
      if (!categoryCheckboxValues.some(check_name_match))
        setCategoryCheckboxValues((values) => [...values, cat]);
    });
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleChkChange = (event) => {
    const chkbox = event.target ?? event;
    const name = chkbox.name;
    const type = chkbox.getAttribute("data-type");
    const value = chkbox.value;
    const checked = chkbox.checked ? "checked" : "";
    setCheckboxes((values) => ({ ...values, [name]: checked }));

    chkbox.checked
      ? setSelectLabels((values) => ({ ...values, [value]: type }))
      : setSelectLabels((values) => {
          const newData = { ...values };
          delete newData[value];
          return newData;
        });

    if (type === "state") {
      (stateCheckboxValues.length > 0
        ? stateCheckboxValues
        : data.aggregations.stateslist.buckets
      ).forEach((bucket) => {
        if (bucket.key === value) {
          bucket["city-list"]["buckets"].forEach((city, index) => {
            city["state"] = bucket.key;
            if (event.target.checked) {
              setCityCheckboxValues((values) => [...values, city]);
            } else {
              setCityCheckboxValues((values) =>
                values.filter((el) => el.key !== city.key)
              );
              setCheckboxes((values) => ({
                ...values,
                ["city" + index]: "",
              }));
              setSelectLabels((values) => {
                const newData = { ...values };
                delete newData[city.key];
                return newData;
              });
              if (document.querySelector(`input[name="city${index}"]`))
                document.querySelector(`[name="city${index}"]`).checked = false;
            }
          });
        }
      });
    }
  };

  const hideFilter = (event) => {
    if (event && event.target && !event.target.matches(".clear-filters")) {
      event.preventDefault();
      document.body.classList.remove("job-filter-active");
    }
  };

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    hideFilter(event);
    removePageQSFromURL();
    updatePageIndex(1);

    let search_params = {},
      category_values = [],
      state_values = [],
      city_values = [],
      kw = document.getElementById("search-kw"),
      categories = document.querySelectorAll(".cat-select"),
      states = document.querySelectorAll(".st-select"),
      cities = document.querySelectorAll(".city-select");

    if (inputs.keywords) {
      search_params["keywords"] = inputs.keywords.trim();
    }

    categories.forEach((el) => {
      if (el.checked) {
        const dataCatArray = el.getAttribute("data-cat-array");
        const catArray = JSON.parse(dataCatArray);
        category_values.push(...catArray);
      }
      setTheTags(el);
    });

    states.forEach((el) => {
      if (el.checked) state_values.push(el.value);
      setTheTags(el);
    });

    cities.forEach((el) => {
      if (el.checked) city_values.push(el.value);
      setTheTags(el);
    });

    if (category_values.length > 0)
      search_params["categories"] = category_values;
    if (state_values.length > 0) search_params["states"] = state_values;
    if (city_values.length > 0) search_params["cities"] = city_values;

    if (category_values.length > 0 || state_values.length > 0)
      saveDropDownData();

    updateJobParams(search_params);

    if (window.innerWidth <= 991) scrollToTopOfJobs();

    navigate(pathname, { replace: true });
  };

  const setTheTags = (input) => {
    const name = input.name;
    const value = input.value;

    if (name === "keywords") {
      if (value) {
        let check_name_match = (obj) => obj.name === "keywords";
        if (!tags.some(check_name_match)) {
          setTags((values) => [...values, { name, value }]);
        } else {
          const mapped = tags.map((values) => {
            const newData = { ...values };
            if (newData["name"] === "keywords") newData["value"] = value;
            return newData;
          });
          setTags(mapped);
        }
      } else {
        setTags((values) =>
          values.filter((el) => el.name !== "keywords")
        );
      }
    } else {
      if (input.checked) {
        let check_name_match = (obj) => obj.name === name;
        if (!tags.some(check_name_match))
          setTags((values) => [...values, { name, value }]);
      } else {
        setTags((values) =>
          values.filter((el) => el.name !== name)
        );
      }
    }
  };

  const handleDropdownToggle = (e, key) => {
    setActiveHeaders((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setTimeout(() => {
      setIsMobileHeaderAnimating(false);
    }, 200);
  };

  const DoMultiChkDropDown = (e, filter_type) => {
    let dropdown = document.querySelector(`[data-drop="${filter_type}"]`);
    let all_dropdowns = document.querySelectorAll(".custom-multichk-detail");

    all_dropdowns.forEach((el) => {
      el.getAttribute("data-drop") === filter_type &&
      el.style.display === "none"
        ? (el.style.display = "block")
        : (el.style.display = "none");
    });
  };

  const handleDropdownKey = (e, key) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleDropdownToggle(e, key);
    }
  };

  const clearAllFilters = () => {
    document.querySelectorAll('input[type="checkbox"]').forEach((el) => {
      if (el.checked) el.click();
    });
    document.getElementById("search-kw").value = "";
    setInputs({});
    handleSubmit();
  };

  const buildCategoryMarkup = () => {
    if (loading && categoryCheckboxValues.length === 0) {
      return (
        <div className="jobs-loading">
          <span>Loading...</span>
        </div>
      );
    } else if (categoryCheckboxValues.length > 0) {
      return (
        <div className="custom-multichk-detail-body" tabIndex={-1}>
          {categoryCheckboxValues.map((row, index) => {
            let categoryArray = [row["job_category_id"] || row["key"]];
            if (row["key"] === "Schools - OT") {
              categoryArray.push("Schools - OT - Telehealth");
            }
            return (
              <div className="cont" key={index}>
                <input
                  type="checkbox"
                  id={"cat" + index}
                  name={"cat" + index}
                  data-type="cat"
                  data-name={row["key"]}
                  className="mulinput cat-select"
                  value={row["job_category_id"] || row["key"]}
                  data-cat-array={JSON.stringify(categoryArray)}
                  checked={checkboxes["cat" + index]}
                  onChange={handleChkChange}
                />
                <label
                  for={"cat" + index}
                  className="label-text-wrap"
                  onClick={(e) => {
                    e.preventDefault();
                    const inputEl = document.getElementById("cat" + index);
                    if (inputEl) {
                      inputEl.checked = !inputEl.checked;
                      handleChkChange({ target: inputEl });
                    }
                  }}
                >
                  {removeMainCat(row["key"])}&nbsp;({row["doc_count"]})
                </label>
              </div>
            );
          })}
        </div>
      );
    } else if (!loading && data?.aggregations?.categorieslist) {
      const buckets = data.aggregations.categorieslist.buckets || [];
      buckets.forEach((row) => {
        row["cleaned_key"] = removeMainCat(row["key"]);
        let categoryArray = [row["job_category_id"] || row["key"]];
        if (row["remote_key"]) {
          categoryArray.push(row["remote_key"]);
        }
        row["categoryArray"] = JSON.stringify(categoryArray);
      });

      buckets.sort((a, b) =>
        a.cleaned_key.toLowerCase() > b.cleaned_key.toLowerCase() ? 1 : -1
      );

      return (
        <div className="custom-multichk-detail-body" tabIndex={-1}>
          {buckets.map((row, index) => (
            <div className="cont" key={index}>
              <input
                type="checkbox"
                id={"cat" + index}
                name={"cat" + index}
                data-type="cat"
                data-name={row["key"]}
                className="mulinput cat-select"
                value={row["job_category_id"] || row["key"]}
                data-cat-array={row["categoryArray"]}
                checked={checkboxes["cat" + index]}
                onChange={handleChkChange}
              />
              <label
                for={"cat" + index}
                className="label-text-wrap"
                onClick={(e) => {
                  e.preventDefault();
                  const inputEl = document.getElementById("cat" + index);
                  if (inputEl) {
                    inputEl.checked = !inputEl.checked;
                    handleChkChange({ target: inputEl });
                  }
                }}
              >
                {row["cleaned_key"]} ({row["doc_count"]})
              </label>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const buildStateMarkup = () => {
    if (loading && !stateCheckboxValues.length > 0) {
      return (
        <div className="jobs-loading">
          <span>Loading...</span>
        </div>
      );
    } else if (stateCheckboxValues.length > 0) {
      return (
        <div className="custom-multichk-detail-body" tabIndex={-1}>
          {stateCheckboxValues.map((row, index) => (
            <div className="cont" key={index}>
              <input
                type="checkbox"
                id={"st" + index}
                name={"st" + index}
                data-type="state"
                data-name={row["key"]}
                className="mulinput st-select"
                value={row["key"]}
                checked={checkboxes["st" + index]}
                onChange={handleChkChange}
              />
              <label for={"st" + index}>
              <a
                href={generateStateLink(row["key"])}
                className="label-text-wrap"
                call="1"
                onClick={(e) => {
                  e.preventDefault();
                  const inputEl = document.getElementById("st" + index);
                  if (inputEl) {
                    inputEl.checked = !inputEl.checked;
                    handleChkChange({ target: inputEl });
                  }
                }}
              >
                {getFullState(row["key"])}
              </a></label>
            </div>
          ))}
        </div>
      );
    } else if (!loading && data?.aggregations?.stateslist) {
      const sortedBuckets = [...data.aggregations.stateslist.buckets].sort(
        (a, b) => a.key.localeCompare(b.key)
      );
      return (
        <div className="custom-multichk-detail-body" tabIndex={-1}>
          {sortedBuckets.map((row, index) => (
            <div className="cont" key={index}>
              <input
                type="checkbox"
                id={"st" + index}
                name={"st" + index}
                data-type="state"
                data-name={row["key"]}
                className="mulinput st-select"
                value={row["key"]}
                checked={checkboxes["st" + index]}
                onChange={handleChkChange}
              />
              <label for={"st" + index}>
              <a
                href={generateStateLink(row["key"])}
                className="label-text-wrap"
                call="2"
                onClick={(e) => {
                  e.preventDefault();
                  const inputEl = document.getElementById("st" + index);
                  if (inputEl) {
                    inputEl.checked = !inputEl.checked;
                    handleChkChange({ target: inputEl });
                  }
                }}
              >
                {getFullState(row["key"])}
              </a>
              </label>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const buildCityMarkup = () => {
    if (cityCheckboxValues.length > 0) {
      return (
        <fieldset
          class={`input-wrap field-group-select fieldset-city ${
            activeHeaders["city"] ? "filter-header-active" : ""
          }`}
          aria-describedby="city-filter-instructions"
        >
          <legend>City</legend>
          <div
            class="custom-multichk custom-multichk-type"
            id="city-filter"
            data-select="city"
            aria-label="Filter by City"
            tabIndex="0"
            role="button"
            aria-expanded={activeHeaders["city"] ? "true" : "false"}
            aria-controls="city-options-wrap"
            onClick={(e) => handleDropdownToggle(e, "city")}
            onKeyDown={(e) => handleDropdownKey(e, "city")}
          >
            {handleSelectLabels("city", "Filter by City")}
            <svg className="arrow-control" width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.03857L9 8.73447L17 1.03857" stroke="#215FD7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </div>
          <div className="positioning-wrap" id="city-options-wrap">
            <div className="custom-multichk-detail city-options" data-drop="city">
              <div className="custom-multichk-detail-body" tabIndex={-1}>
                {cityCheckboxValues?.map((row, index) => (
                  <div className="cont" key={index}>
                    <input
                      type="checkbox"
                      id={"city" + index}
                      name={"city" + index}
                      data-type="city"
                      data-name={row["key"]}
                      className="mulinput city-select"
                      value={row["key"]}
                      checked={checkboxes["city" + index]}
                      onChange={handleChkChange}
                    />
                    <labe for={"city" + index}>
                    <a
                      href={generateCityLink(row["key"], row["state"])}
                      className="label-text-wrap"
                      onClick={(e) => {
                        e.preventDefault();
                        const inputEl = document.getElementById("city" + index);
                        if (inputEl) {
                          inputEl.checked = !inputEl.checked;
                          handleChkChange({ target: inputEl });
                        }
                      }}                      
                    >
                      {row["key"]}
                      {row["doc_count"] && ` (${row["doc_count"]})`}
                    </a>
                    </labe>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </fieldset>
      );
    }
    return null;
  };

  return (
    <>
      <aside className="sec-job-search sec-blue">
        <div className="container wrap">
          <div className="row">
            <div className="job-search-form hero__job-search-bar">
              <form
                id="header-job-search"
                role="search"
                className="jobs__search-form search-form"
                onSubmit={handleSubmit}
                aria-label="Search our jobs"
              >
                <div className="input-wrap">
                  <label htmlFor="search-kw">Keyword</label>
                  <input
                    type="search"
                    id="search-kw"
                    className="search-field"
                    placeholder="Job Title"
                    name="keywords"
                    title="Search Jobs by Keyword"
                    aria-label="Input job keywords here"
                    value={inputs.keywords}
                    onChange={handleChange}
                  />
                </div>
                <fieldset
                  class={`input-wrap field-group-select fieldset-category ${
                    activeHeaders["cat"] ? "filter-header-active" : ""
                  }`}
                  aria-describedby="cat-filter-instructions"
                >
                  <legend>Category</legend>
                  <div
                    className="custom-multichk custom-multichk-cat"
                    id="cat-filter"
                    name="search-cat"
                    data-select="cat"
                    aria-label="Filter by Category"
                    tabIndex="0"
                    role="button"
                    aria-expanded={activeHeaders["cat"] ? "true" : "false"}
                    aria-controls="cat-options-wrap"
                    onClick={(e) => handleDropdownToggle(e, "cat")}
                    onKeyDown={(e) => handleDropdownKey(e, "cat")}
                  >
                    {handleSelectLabels("cat", "Filter by Category")}
                    <svg className="arrow-control" width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.03857L9 8.73447L17 1.03857" stroke="#215FD7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
                  </div>
                  <div id="cat-options-wrap" className="positioning-wrap">
                    <div className="custom-multichk-detail cat-options">
                      {buildCategoryMarkup()}
                    </div>
                  </div>
                </fieldset>
                <fieldset
                  class={`input-wrap field-group-select fieldset-state ${
                    activeHeaders["state"] ? "filter-header-active" : ""
                  }`}
                  aria-describedby="state-filter-instructions"
                >
                  <legend>State</legend>
                  <div
                    className="custom-multichk custom-multichk-state"
                    id="state-filter"
                    data-select="state"
                    aria-label="Filter by State"
                    tabIndex="0"
                    role="button"
                    aria-expanded={activeHeaders["state"] ? "true" : "false"}
                    aria-controls="state-options-wrap"
                    onClick={(e) => handleDropdownToggle(e, "state")}
                    onKeyDown={(e) => handleDropdownKey(e, "state")}
                  >
                    {handleSelectLabels("state", "Filter by State")}
                    <svg className="arrow-control" width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.03857L9 8.73447L17 1.03857" stroke="#215FD7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
                  </div>
                  <div aria-controls="state-options-wrap" className="positioning-wrap">
                    <div className="custom-multichk-detail state-options" data-drop="state">
                      {buildStateMarkup()}
                    </div>
                  </div>
                </fieldset>
                {buildCityMarkup()}
                <div className="input-wrap input-wrap-submit">
                  <button
                    type="submit"
                    className="button primary-btn primary-btn--white"
                    id="SearchSubmit"
                    aria-label="Click here to Search for Jobs"
                    tabIndex={0}
                  >
                    <span className="primary-btn__inner">Search Jobs</span>
                  </button>
                  <button
                    className="job-filter-clearfacets"
                    type="button"
                    onClick={clearAllFilters}
                  >
                    <span className="clear-filters">
                      Clear Filters{" "}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-x"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default JobSearch;